import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction } from '.';
import { IPostGamePurchase } from '../interfaces/IPostGamePurchase';
import { ICreateInvoiceRequest, IUpdateInvoiceRequest, PaymentStatusType } from '../interfaces/IInvoice';
import { ILoadedInvoiceAction, actionCreators as Invoice, LOADED_INVOICE } from './Invoice';
import { Routes, navigateTo } from '../helpers/routes';
import PremiumPtv from '../service/premiumptv';
import IPrice from '../interfaces/IPrice';
import { PaymentMethodTypes } from './Payments';
import { resolveLocalizedEntry } from '../helpers/localization';

export interface IPremiumPtvState {
    loading: boolean;
    playerGuid?: string;
    gameResultId?: number;
    siteUrlKey?: string;
    purchaseDetails?: IPostGamePurchase;
    selectedPlayers?: string[];
    price?: IPrice;
    purchasePlayerCount?: number;
    creditPlayerCount?: number;
    purchaseName?: string;
}

export const LOADING_PTV_DETAILS: 'LOADING_PTV_DETAILS' = 'LOADING_PTV_DETAILS';
export const LOADED_PTV_DETAILS: 'LOADED_PTV_DETAILS' = 'LOADED_PTV_DETAILS';
export const SET_SELECTED_PLAYERS: 'SET_SELECTED_PLAYERS' = 'SET_SELECTED_PLAYERS';

export interface ILoadingPtvDetailsAction extends Action {
    type: typeof LOADING_PTV_DETAILS;
    gameResultId: number;
    playerGuid: string;
    siteUrlKey: string;
}

export interface ILoadedPtvDetailsAction extends Action {
    type: typeof LOADED_PTV_DETAILS;
    purchaseDetails: IPostGamePurchase;
    purchaseName?: string;
}

export interface ISetSelectedPlayersAction extends Action {
    type: typeof SET_SELECTED_PLAYERS;
    selectedPlayers: string[];
    price: IPrice;
    purchasePlayerCount: number;
    creditPlayerCount: number;
}

export type KnownPremiumPtvActions = 
    ILoadingPtvDetailsAction | 
    ILoadedPtvDetailsAction | 
    ISetSelectedPlayersAction;

export const actionCreators = {
    loadPtvDetails:
        (
            gameResultId: number,
            playerGuid: string,
            siteUrlKey: string
        ): IAppThunkAction<KnownPremiumPtvActions> =>
        (dispatch: any, getState: any) => {
            dispatch({
                type: LOADING_PTV_DETAILS,
                gameResultId: gameResultId,
                playerGuid: playerGuid.toLowerCase(),
                siteUrlKey: siteUrlKey
            } as ILoadingPtvDetailsAction);

            PremiumPtv.GetPostGamePurchaseDetails(siteUrlKey, gameResultId, playerGuid)
                .then((response) => {
                    const state = getState() as IApplicationState;
                    
                    const locale = state.intl.selectedLanguage.code;
                    const siteDefaultLocale = state.sites.selectedSite?.defaultLocale;

                    const languageData = resolveLocalizedEntry(response.translations, locale, siteDefaultLocale);

                    dispatch({
                        type: LOADED_PTV_DETAILS,
                        purchaseDetails: response,
                        purchaseName: languageData?.name ?? response.alias
                    } as ILoadedPtvDetailsAction);
                    
                    if(state.premiumPtv.selectedPlayers && state.premiumPtv.selectedPlayers.length > 0) {
                        const newSelectedPlayers = state.premiumPtv.selectedPlayers.filter(p => response.players.some(rp => rp.playerGuid === p));
                        const price = response.prices.find(p => p.fromQuantity === 1)?.price;
                        dispatch(actionCreators.setSelectedPlayers(newSelectedPlayers, price!));
                        return;
                    }

                    const purchasePlayer = response.players.find(p => p.playerGuid.toLowerCase() === playerGuid.toLowerCase());
                    if(purchasePlayer) {
                        const price = response.prices.find(p => p.fromQuantity === 1)?.price;
                        if(price) {
                            dispatch(actionCreators.setSelectedPlayers([ purchasePlayer.playerGuid ], price));
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error loading PTV details', error);
                });
        },

    consumeCredits:(): IAppThunkAction<KnownPremiumPtvActions> =>
        (dispatch: any, getState: any) => {
            const state = getState() as IApplicationState;
            if(!state.premiumPtv.purchaseDetails || state.premiumPtv.creditPlayerCount === undefined || state.premiumPtv.creditPlayerCount <= 0) {
                return;
            }
            const bookingId = state.premiumPtv.purchaseDetails.bookingId;
            const playerGuid = state.premiumPtv.playerGuid;
            const siteUrlKey = state.premiumPtv.siteUrlKey;

            if(bookingId === undefined || playerGuid === undefined || siteUrlKey === undefined)
                return;

            if(!state.premiumPtv.purchasePlayerCount || state.premiumPtv.purchasePlayerCount === 0) {
                dispatch(navigateTo(Routes.Processing));
            }

            const creditPlayers = state.premiumPtv.selectedPlayers?.slice(0, state.premiumPtv.creditPlayerCount) ?? [];

            console.log('Consuming credits for booking ' + bookingId + ' and player ' + playerGuid);

            PremiumPtv.ConsumeCredits(siteUrlKey, bookingId, playerGuid, creditPlayers)
                .then((_response) => {
                    console.log('Credits consumed successfully');
                    // set the invoice status to paid
                    if(!state.premiumPtv.purchasePlayerCount || state.premiumPtv.purchasePlayerCount === 0) {
                        console.log('No purchase required, marking invoice as paid');
                        dispatch({
                            type: LOADED_INVOICE,
                            charge: state.invoice.charge!,
                            invoiceId: state.invoice.invoiceId ?? 0,
                            siteId: state.premiumPtv.purchaseDetails?.siteId ?? 0,
                            paymentStatusTypeId: PaymentStatusType.Approved
                        } as ILoadedInvoiceAction);
                        // Go to the complete page
                        dispatch(navigateTo(Routes.Complete, siteUrlKey));
                    }
                    else {
                        // We need to wait for the purchase to complete before we can send the user to the complete page
                        // This will be handled in the invoice status check
                    }
                })
                .catch((error) => {
                    console.error('Error consuming credits', error);
                });
        },

    setSelectedPlayers:
        (selectedPlayers: string[], price: IPrice): IAppThunkAction<KnownPremiumPtvActions> => (dispatch: any, getState: any) => {
            // Submit and invoice update to get new charge
            const state = getState() as IApplicationState;

            const skipPlayerCount = state.premiumPtv.purchaseDetails?.unusedCredits;
            const creditPlayerCount = skipPlayerCount && skipPlayerCount > 0 ? 
                (skipPlayerCount > selectedPlayers.length ? selectedPlayers.length : skipPlayerCount) : 0;

            const purchasePlayerCount = selectedPlayers.length - creditPlayerCount;

            dispatch({
                type: SET_SELECTED_PLAYERS,
                selectedPlayers: selectedPlayers,
                price: price,
                creditPlayerCount: creditPlayerCount,
                purchasePlayerCount: purchasePlayerCount
            } as ISetSelectedPlayersAction);

            if(purchasePlayerCount <= 0) {
                if (selectedPlayers.length === 0) {
                    dispatch(Invoice.clearInvoice());
                    return;
                }

                // If the user has selected less players than they have unused credits, we don't need to generate a new invoice
                // Instead, load in an empty invoice
                dispatch({
                    type: LOADED_INVOICE,
                    charge: {
                        total: 0,
                        cost: 0,
                        tax: 0,
                        fee: 0,
                        taxInclusive: true,
                        currencyCode: state.premiumPtv.purchaseDetails?.site.paypalCode,
                        currencyLocale: state.premiumPtv.purchaseDetails?.site.defaultLocale,
                        paymentMethodTypeId: PaymentMethodTypes.Credit
                    },
                    invoiceId: 0,
                    siteId: 0,
                    paymentStatusTypeId: PaymentStatusType.AwaitingPayment
                } as ILoadedInvoiceAction);
                return;
            }

            // Purchase players are the remaining players after unused credits have been applied
            const purchasePlayers = selectedPlayers.slice(creditPlayerCount);

            if (state.premiumPtv.purchaseDetails) {
                if(state.invoice.invoiceId) {
                    const invoiceUpdate = {
                        digitalAddOns: [
                            {
                                bookingId: state.premiumPtv.purchaseDetails.bookingId,
                                digitalAddOnOptionId: state.premiumPtv.purchaseDetails.digitalAddOnOptionId,
                                quantity: purchasePlayers.length,
                                playerGuids: purchasePlayers
                            }
                        ]
                    } as IUpdateInvoiceRequest

                    dispatch(Invoice.updateInvoice(state.premiumPtv.purchaseDetails.siteId, state.invoice.invoiceId, invoiceUpdate));
                } else {
                    const invoiceCreate = {
                        buyerPlayerGuid: state.premiumPtv.playerGuid,
                        digitalAddOns: [
                            {
                                bookingId: state.premiumPtv.purchaseDetails.bookingId,
                                digitalAddOnOptionId: state.premiumPtv.purchaseDetails.digitalAddOnOptionId,
                                quantity: purchasePlayers.length,
                                playerGuids: purchasePlayers
                            }
                        ]
                    } as ICreateInvoiceRequest

                    dispatch(Invoice.createInvoice(state.premiumPtv.purchaseDetails.siteId, invoiceCreate));
                }
            }
        }
};

const unloadedState: IPremiumPtvState = {
    loading: false
};

export const reducer: Reducer<IPremiumPtvState> = (
    state: IPremiumPtvState | undefined,
    incomingAction: Action
): IPremiumPtvState => {
    if (state === undefined) return unloadedState;

    const action: KnownPremiumPtvActions = incomingAction as KnownPremiumPtvActions;
    switch (action.type) {
        case LOADING_PTV_DETAILS:
            return {
                ...state,
                loading: true,
                gameResultId: action.gameResultId,
                playerGuid: action.playerGuid,
                siteUrlKey: action.siteUrlKey,
                purchaseDetails: undefined,
                purchaseName: undefined,
            };
        case LOADED_PTV_DETAILS:
            return {
                ...state,
                loading: false,
                purchaseDetails: action.purchaseDetails,
                purchaseName: action.purchaseName
            };
        case SET_SELECTED_PLAYERS:
            return {
                ...state,
                selectedPlayers: action.selectedPlayers,
                price: action.price,
                purchasePlayerCount: action.purchasePlayerCount,
                creditPlayerCount: action.creditPlayerCount
            };
        default:
            return state;
    }
};
